// Referees list with control elements. In the admin panel the organization's
// referee assigners head it, each list with its own invite.

import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useOrgUsers } from '../utils/hooks';
import { UsersRoles } from '../utils/enums';
import InviteButton from './InviteButton';
import FilterInput from './FilterInput';
import Spinner from './Spinner';
import Avatar from './Avatar';
import './RefereesList.scss';

// One query for both: `role in` narrows the organization branch of the rules
const refereeRoles = [UsersRoles.REFEREE, UsersRoles.REFEREE_ASSIGNER];

const byName = (a, b) =>
  a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });

function PersonRow({ person, path }) {
  const { id, name, phone, email } = person;

  return (
    <Link to={`${path}/${id}`} className='referee'>
      <Avatar user={person} size={64} className='col-1' />
      <div className='col-2'>
        <Avatar user={person} size={32} />
        <p className='upper font-medium'>{name}</p>
      </div>
      <p className='col-3'>{email}</p>
      <p className='col-4'>{phone}</p>
      <p className='col-34'>
        {email}
        <br />
        {phone}
      </p>
    </Link>
  );
}

export default function RefereesList({ withAssigners = false }) {
  // The organization's referees, read only while this tab is open
  const { users, isLoaded } = useOrgUsers(
    withAssigners ? refereeRoles : UsersRoles.REFEREE,
  );

  const [searchQuery, setSearchQuery] = useState('');

  const sortedReferees = useMemo(
    () => users.filter((u) => u.role === UsersRoles.REFEREE).toSorted(byName),
    [users],
  );

  const assigners = useMemo(
    () =>
      users
        .filter((u) => u.role === UsersRoles.REFEREE_ASSIGNER)
        .toSorted(byName),
    [users],
  );

  const searchValues = useMemo(() => {
    return sortedReferees.map((r) => [r.name]);
  }, [sortedReferees]);

  const displayedReferees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedReferees;
    return sortedReferees.filter((_, i) =>
      searchValues[i].some((val) => val && val.toLowerCase().includes(q)),
    );
  }, [sortedReferees, searchValues, searchQuery]);

  return (
    <div className='referees-list'>
      {withAssigners && (
        <>
          <div className='assigners'>
            <div className='controls'>
              <h5>Referee assigners</h5>
              <InviteButton
                tokenCreatePayload={{ role: UsersRoles.REFEREE_ASSIGNER }}
                invited='referee assigners'
              >
                Invite assigners
              </InviteButton>
            </div>
            <div className='tab-body'>
              {assigners.map((assigner) => (
                <PersonRow
                  key={assigner.id}
                  person={assigner}
                  path='/referee-assigners'
                />
              ))}
            </div>
          </div>
          <hr />
        </>
      )}
      <div className='sticky-top'>
        <div className='controls'>
          <div className='filters'>
            <FilterInput
              className='search'
              placeholder='Search'
              value={searchQuery}
              onChange={setSearchQuery}
              small
            />
          </div>

          <InviteButton
            tokenCreatePayload={{ role: UsersRoles.REFEREE }}
            invited='referees'
          >
            Invite referees
          </InviteButton>
        </div>
        {!(searchQuery.trim() && displayedReferees.length === 0) && (
          <ul className='tab-header'>
            <li className='col-1' />
            <li className='col-2'>Name</li>
            <li className='col-3'>E-mail</li>
            <li className='col-4'>Phone</li>
            <li className='col-34'>Contacts</li>
          </ul>
        )}
      </div>
      {!isLoaded && <Spinner show inline />}
      {searchQuery.trim() && displayedReferees.length === 0 ? (
        <h5 className='name'>No results found for your search</h5>
      ) : (
        <div className='tab-body'>
          {displayedReferees.map((referee) => (
            <PersonRow key={referee.id} person={referee} path='/referees' />
          ))}
        </div>
      )}
    </div>
  );
}
